import React from 'react';

import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { RovoHexIcon } from '@atlaskit/logo/rovo-hex/icon';
import { ExitingPersistence, FadeIn } from '@atlaskit/motion';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags';
import { Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { AgentVerifiedIcon } from '@atlaskit/rovo-agent-components/agent-verified';
import type { AgentCreatorType } from '@atlaskit/rovo-agent-components/common/types';
import { isForgeAgentByCreatorType } from '@atlaskit/rovo-agent-components/common/utils/is-forge-agent';
import { AgentAvatar } from '@atlaskit/rovo-agent-components/ui/AgentAvatar';
import { token } from '@atlaskit/tokens';
import { type Agent } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { isDefaultRovoChat } from '@atlassian/conversation-assistant-utils/default-agent';

import { useGlobalConfig } from '../../../../common/utils/global-config/useGlobalConfig';
import { useUIConfig } from '../../../../common/utils/ui-config/useUIConfig';
import { AgentIdentityAccountDisplay } from '../../../agent-identity-account-display/agent-identity-account-display';

const styles = cssMap({
	container: {
		paddingInline: token('space.500'),
	},
});

type RovoAgentHeaderProps = {
	agent: Agent;
	versionPicker?: React.ReactNode;
};

export const RovoAgentHeader = ({
	agent,
	versionPicker,
}: RovoAgentHeaderProps): React.JSX.Element | null => {
	const { fullScreen } = useUIConfig();
	const isDefaultRovoAgent =
		agent.is_default ||
		isDefaultRovoChat({
			agentId: agent.id,
			agentNamedId: agent.named_id,
		});
	const mentionAgentsEnabled = isExperimentEnabled('rovo_chat_mention_agents');
	const shouldUseRelayAgentIdentityDisplay = mentionAgentsEnabled && !isDefaultRovoAgent;

	const { siteId } = useGlobalConfig();

	return (
		<ExitingPersistence appear exitThenEnter>
			<FadeIn duration="large" entranceDirection="bottom" exitDirection="top">
				{(props) => (
					<div {...props}>
						<Stack
							alignBlock="center"
							alignInline="center"
							space="space.100"
							xcss={fullScreen ? undefined : styles.container}
						>
							{mentionAgentsEnabled && isDefaultRovoAgent ? (
								<>
									<RovoHexIcon label="" size="large" />
									<Heading size="large">Rovo</Heading>
								</>
							) : shouldUseRelayAgentIdentityDisplay ? (
								<Inline space="space.075" alignBlock="center">
									<AgentIdentityAccountDisplay
										agentAri={agent.agentAri}
										agentId={agent.id}
										cloudId={siteId}
										identityAccountId={agent.identity_account_id}
										size="large"
										testId="rovo-agent-header-relay-name"
									/>
									{agent.is_verified && fg('rovo_agents_agent_verification') ? (
										<AgentVerifiedIcon adjacentTextSize="headingLarge" />
									) : null}
								</Inline>
							) : (
								<>
									<AgentAvatar
										agentId={agent.id}
										agentNamedId={agent.external_config_reference ?? agent.named_id}
										agentIdentityAccountId={agent.identity_account_id}
										size="large"
										isForgeAgent={
											fg('rovo_agent_support_a2a_avatar')
												? isForgeAgentByCreatorType(agent.creator_type as AgentCreatorType)
												: undefined
										}
										forgeAgentIconUrl={fg('rovo_agent_support_a2a_avatar') ? agent.icon : undefined}
									/>
									<Inline space="space.075">
										<Heading size="large">{agent.name}</Heading>
										{agent.is_verified && fg('rovo_agents_agent_verification') ? (
											<AgentVerifiedIcon adjacentTextSize="headingLarge" />
										) : null}
									</Inline>
								</>
							)}
							<Text color="color.text.subtlest" align="center">
								{agent.description}
							</Text>
							{versionPicker}
						</Stack>
					</div>
				)}
			</FadeIn>
		</ExitingPersistence>
	);
};
